// Decides when the referral upsell dialog opens.
//
// Rules: an allowlisted public page, a settling delay, and a 7-day cooldown since
// it was last shown. The cooldown timestamp is written when the dialog OPENS, not
// when it closes — a visitor who ignores it and navigates to another public page
// must not get it again on the next route change. That is the one behavioural
// difference from usePackageUpsellPrompt, which stamps on close because it only
// ever mounts on a single form.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as safePosthog from "../analytics/safePosthog";
import { AnalyticsEvents } from "../analytics/events";
import {
  isReferralUpsellPath,
  REFERRAL_UPSELL_COOLDOWN_MS,
  REFERRAL_UPSELL_DELAY_MS,
  REFERRAL_UPSELL_LAST_SHOWN_KEY,
} from "../constants/referralUpsell";

export type ReferralUpsellDismissMethod = "maybe_later" | "close";

function readLastShown(): number | null {
  try {
    const raw = localStorage.getItem(REFERRAL_UPSELL_LAST_SHOWN_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // localStorage unavailable (private browsing) — treat as never shown. The
    // dialog then behaves as session-only, which is the safe direction.
    return null;
  }
}

export function isReferralUpsellDue(now = Date.now()): boolean {
  const lastShown = readLastShown();
  if (lastShown === null) return true;
  return now - lastShown >= REFERRAL_UPSELL_COOLDOWN_MS;
}

function stampShown(): void {
  try {
    localStorage.setItem(REFERRAL_UPSELL_LAST_SHOWN_KEY, String(Date.now()));
  } catch {
    // Best-effort. Worst case the dialog reappears next visit.
  }
}

export function useReferralUpsellPrompt() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Guards against a second open in the same page load: once the timer has fired
  // (or been armed and resolved), route changes must not re-arm it.
  const handledRef = useRef(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (handledRef.current) return;
    if (!isReferralUpsellPath(pathname)) return;
    if (!isReferralUpsellDue()) {
      // Nothing to show this visit — stop re-checking on every navigation.
      handledRef.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      // Re-check the path at fire time: the visitor may have navigated somewhere
      // excluded (a booking form, checkout) during the delay.
      if (!isReferralUpsellPath(window.location.pathname)) return;

      handledRef.current = true;
      shownAtRef.current = Date.now();
      stampShown();
      safePosthog.capture(AnalyticsEvents.REFERRAL_UPSELL_SHOWN, {
        path: window.location.pathname,
      });
      setOpen(true);
    }, REFERRAL_UPSELL_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [pathname]);

  const close = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      safePosthog.capture(event, {
        ms_open: shownAtRef.current ? Date.now() - shownAtRef.current : null,
        ...properties,
      });
      setOpen(false);
    },
    [],
  );

  const dismiss = useCallback(
    (method: ReferralUpsellDismissMethod = "close") => {
      close(AnalyticsEvents.REFERRAL_UPSELL_DISMISSED, { method });
    },
    [close],
  );

  /** `action` distinguishes a guest clicking through to signup from a member copying/sharing. */
  const accept = useCallback(
    (action: "register" | "copy_code" | "share_link") => {
      close(AnalyticsEvents.REFERRAL_UPSELL_ACCEPTED, { action });
    },
    [close],
  );

  return { open, dismiss, accept };
}
